// Single wrapper around posthog-js for the PostHog "engagement slice" — the activation funnel,
// retention, and route-scoped session replay. EVERY call-site goes through this module so two
// invariants live in exactly one place:
//
//   1. STRUCTURE-ONLY (non-negotiable, mirrors the client_error_events beacon rule): event bodies
//      carry ids / types / verbs / counts ONLY — never message text, entity names, titles, or
//      summaries. That is why `autocapture` is OFF below: PostHog autocapture attaches the *text*
//      of clicked elements to $autocapture events, and it fires on ALL routes (unlike replay,
//      which we route-scope). On a content route (/briefing, /map, …) that text is ingested
//      content. Keeping autocapture off makes every emitted event an explicit, hand-audited call.
//
//   2. NO-KEY NO-OP: with no VITE_POSTHOG_KEY the module never inits and every function returns
//      immediately, so dev / MSW / CI stay completely silent and never touch the network.

import posthog from 'posthog-js';

// Reverse-proxy path (vercel.json rewrites /ingest → PostHog US). Same-origin, so it survives
// ad-blockers and needs no CSP connect-src exception. Defaults to /ingest when unset.
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || '/ingest';

// Flipped true only once init succeeds. Every emit guards on it, so nothing fires before init or
// when no key is configured.
let ready = false;

export function initAnalytics(): void {
  // Public project key. Absent ⇒ no init, so the whole module stays inert. Read at call time
  // (not module load) so tests can toggle it and so a missing key is a clean no-op, not a throw.
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key || ready) return;
  posthog.init(key, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false, // HashRouter: autocapture $pageview never fires on hash nav — pageview() sends it manually
    autocapture: false, // see STRUCTURE-ONLY above — off by construction, not a preference
    disable_session_recording: true, // replay OFF by default; useSessionReplayScope turns it on ONLY for allowlisted routes
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*', // backstop only: mask ALL text in any recording (route-scoping is the real control)
    },
  });
  ready = true;
}

type EventProps = Record<string, string | number | boolean | null>;

export function track(event: string, props?: EventProps): void {
  if (!ready) return;
  posthog.capture(event, props);
}

// Manual pageview (HashRouter). `route` is the hash path only (e.g. "/briefing") — no ids, no content.
export function pageview(route: string): void {
  if (!ready) return;
  posthog.capture('$pageview', { route });
}

export function identifyUser(userId: string): void {
  if (!ready) return;
  posthog.identify(userId);
}

export function setTeamGroup(teamId: string): void {
  if (!ready) return;
  posthog.group('team', teamId);
}

export function resetUser(): void {
  if (!ready) return;
  posthog.reset();
}

// Session replay start/stop — driven ONLY by useSessionReplayScope's route allowlist. Never call
// startSessionReplay() from a content route; the allowlist (default-deny) is the safety control.
export function startSessionReplay(): void {
  if (!ready) return;
  posthog.startSessionRecording();
}

export function stopSessionReplay(): void {
  if (!ready) return;
  posthog.stopSessionRecording();
}
